/**
 * Публичная конфигурация сайта: внешние ссылки и видимость разделов.
 *
 * Значения приходят из amazing_backend, но в коде лежат те же умолчания,
 * что и в каталоге на бэкенде — страница остаётся рабочей, даже если
 * запрос не прошёл.
 */
import React from 'react';

const BASE = process.env.REACT_APP_ADMIN_API_URL ?? '';
const AUTH_BASE = process.env.REACT_APP_AUTH_URL ?? 'http://localhost:3000';

export type SectionVisibility = 'on' | 'hidden' | 'off';

/** Юридический документ в том виде, в каком он нужен ссылкам сайта */
export interface LegalDoc {
  slug: string;
  title: string;
  url: string;
  version: string;
}

/**
 * Пока документ не заведён в разделе «Документы», ссылка ведёт на прежний PDF.
 * Как только у активной версии появится адрес — сайт подхватит его сам.
 */
const FALLBACK_DOCS: Record<string, LegalDoc> = {
  tos: {
    slug: 'tos', version: '', title: 'Пользовательское соглашение',
    url: 'https://amazingrp.ru/files/user-agreement.pdf',
  },
  offer: {
    slug: 'offer', version: '', title: 'Публичная оферта',
    url: 'https://amazingrp.ru/files/public-offer.pdf',
  },
  privacy: {
    slug: 'privacy', version: '', title: 'Политика конфиденциальности',
    url: 'https://amazingrp.ru/files/personal-data-policy.pdf',
  },
};

/** Умолчания дублируют каталог в amazing_backend/src/services/siteLinks.ts */
const FALLBACK_LINKS: Record<string, string> = {
  launcher_setup:   '',
  teamspeak_client: 'https://amazingrp.ru/files/teamspeak3.zip',
  arma3_store:      'https://store.steampowered.com/app/107410/Arma_3/',
  ts_connect:       'ts3server://ts.amazingrp.ru',
  help_video:       'https://youtu.be/e2GpA1zxVIs',
  site_home:        'https://amazingrp.ru',
  cabinet:          'https://amazingrp.ru/panel',
  forum:            'https://amazingrp.ru/forum/',
  vk:               'https://vk.com/amazingliferpg',
  discord:          'http://discord.amazingrp.ru',
  support_vk:       'https://vk.com/im?sel=-135945913',
  support_discord:  'https://discord.gg/YaMS8dUxHp',
};

interface SiteConfig {
  links: Record<string, string>;
  /** Активные версии юридических документов по slug */
  documents: Record<string, LegalDoc>;
  newsSection: SectionVisibility;
  /** Ответ бэкенда получен: до этого видимость разделов ещё не известна */
  ready: boolean;
}

const INITIAL: SiteConfig = {
  links: FALLBACK_LINKS,
  documents: FALLBACK_DOCS,
  newsSection: 'on',
  ready: false,
};

const SiteConfigContext = React.createContext<SiteConfig>(INITIAL);

export const SiteConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = React.useState<SiteConfig>(INITIAL);

  React.useEffect(() => {
    let alive = true;

    const load = async () => {
      const [links, sections, docs] = await Promise.all([
        fetch(`${BASE}/api/links`)
          .then(r => (r.ok ? r.json() as Promise<{ links: Record<string, string> }> : null))
          .catch(() => null),
        fetch(`${BASE}/api/settings/public`)
          .then(r => (r.ok ? r.json() as Promise<{ section_news_site?: SectionVisibility }> : null))
          .catch(() => null),
        // Активные версии юридических документов живут в amazing_auth
        fetch(`${AUTH_BASE}/legal/documents`)
          .then(r => (r.ok ? r.json() as Promise<Array<LegalDoc & { url: string | null }>> : null))
          .catch(() => null),
      ]);
      if (!alive) return;

      const documents = { ...FALLBACK_DOCS };
      for (const d of docs ?? []) {
        // Документ без адреса показывать негде — оставляем прежний PDF
        if (!d.url) continue;
        documents[d.slug] = { slug: d.slug, title: d.title, url: d.url, version: d.version };
      }

      setConfig({
        // Пустые значения с бэкенда не затирают умолчания
        links: { ...FALLBACK_LINKS, ...Object.fromEntries(
          Object.entries(links?.links ?? {}).filter(([, url]) => !!url),
        ) },
        documents,
        newsSection: sections?.section_news_site ?? 'on',
        ready: true,
      });
    };

    void load();
    return () => { alive = false; };
  }, []);

  return (
    <SiteConfigContext.Provider value={config}>{children}</SiteConfigContext.Provider>
  );
};

export function useSiteConfig(): SiteConfig {
  return React.useContext(SiteConfigContext);
}

/** Адрес внешней ссылки по ключу каталога. */
export function useLink(key: string): string {
  return React.useContext(SiteConfigContext).links[key] ?? '';
}

/** Активная версия юридического документа по slug. */
export function useLegalDoc(slug: string): LegalDoc | undefined {
  return React.useContext(SiteConfigContext).documents[slug];
}
